/**
 * Connector Interactions Page
 * Full-page interaction list with sync history tab
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Database, Clock, RotateCw, Loader2, ChevronRight, Search } from 'lucide-react';
import Pagination from '@shared/components/Pagination';
import usePagination from '@shared/hooks/usePagination';
import DateRangeFilter from '../components/dashboard/DateRangeFilter';
import EmptyState from '../components/shared/EmptyState';
import Spinner from '../components/shared/Spinner';
import RefreshButton from '../components/shared/RefreshButton';
import useDateRange from '../hooks/useDateRange';
import {
  getConnector,
  getInteractions,
  getSyncHistory,
  retryInteractionProcessing,
} from '../services/connectorService';
import { getPipelineStatus, retryPipelineStep } from '../services/analyticsService';
import { agentLabel } from '../utils/formatters';
import { usePermissions } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';

// Color and label constants
const TYPE_COLORS = {
  call: 'bg-blue-100 text-blue-700',
  chat: 'bg-green-100 text-green-700',
  email: 'bg-purple-100 text-purple-700',
};

const INTERACTION_STATUS_COLORS = {
  completed: 'text-green-600',
  missed: 'text-red-600',
  failed: 'text-red-600',
  busy: 'text-amber-600',
  no_answer: 'text-amber-600',
};

const PROCESSING_STATUS_COLORS = {
  pending: 'bg-gray-100 text-gray-500',
  audio_downloading: 'bg-blue-100 text-blue-700',
  audio_downloaded: 'bg-blue-100 text-blue-700',
  audio_failed: 'bg-red-100 text-red-700',
  transcribing: 'bg-purple-100 text-purple-700',
  transcribed: 'bg-purple-100 text-purple-700',
  transcription_failed: 'bg-red-100 text-red-700',
  completed: 'bg-green-100 text-green-700',
  skipped: 'bg-gray-100 text-gray-400',
};

const PROCESSING_STATUS_LABELS = {
  pending: 'Pending',
  audio_downloading: 'Downloading',
  audio_downloaded: 'Downloaded',
  audio_failed: 'Audio Failed',
  transcribing: 'Transcribing',
  transcribed: 'Transcribed',
  transcription_failed: 'Transcr. Failed',
  completed: 'Completed',
  skipped: 'Skipped',
};

const STATUS_COLORS = {
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  running: 'bg-blue-100 text-blue-700',
  pending: 'bg-gray-100 text-gray-500',
};

const STEP_LABELS = { sentiment: 'S', qa: 'QA', escalation: 'E', location: 'L' };
const STEP_TOOLTIPS = { sentiment: 'Sentiment', qa: 'QA Report', escalation: 'Escalation', location: 'Location' };
const STEP_STATUS_STYLES = {
  completed: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
  processing: 'bg-blue-100 text-blue-700',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  if (seconds === 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatSyncDuration = (startedAt, completedAt) => {
  if (!completedAt) return 'Running...';
  const start = new Date(startedAt);
  const end = new Date(completedAt);
  const diffSec = Math.floor((end - start) / 1000);
  return formatDuration(diffSec);
};

/* ---------- Analytics Pipeline Badges ---------- */

// `onRetry` is withheld by the parent when the caller lacks analytics:reports.
// The pointer cursor below keys off it too, so an ungated badge never looks
// clickable while doing nothing.
const AnalyticsBadges = ({ pipelineSteps, onRetry, retryingStep }) => {
  if (!pipelineSteps || pipelineSteps.length === 0) {
    return <span className="text-[9px] text-gray-400">—</span>;
  }
  return (
    <div className="flex items-center gap-0.5">
      {pipelineSteps.map((step) => (
        <span
          key={step.step_type}
          title={`${STEP_TOOLTIPS[step.step_type] || step.step_type}: ${step.status}${step.error ? ' — ' + step.error : ''}`}
          className={`text-[9px] px-1 py-0.5 rounded font-medium cursor-default ${STEP_STATUS_STYLES[step.status] || 'bg-gray-100 text-gray-500'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (step.status === 'failed' && onRetry) onRetry(step.step_type);
          }}
          style={step.status === 'failed' && onRetry ? { cursor: 'pointer' } : {}}
        >
          {retryingStep === step.step_type ? '...' : STEP_LABELS[step.step_type] || step.step_type}
        </span>
      ))}
    </div>
  );
};

/* ---------- Main Page ---------- */

const ConnectorInteractionsPage = () => {
  const { connectorId } = useParams();
  const navigate = useNavigate();
  const { page, pageSize, setPage, setPageSize, reset } = usePagination(1, 20);
  const { from, to, onChange: onDateChange } = useDateRange();

  // Two retries on the same row hit two different services, so they need two
  // different permissions: the pipeline-step retry goes to analytics_service,
  // the whole-interaction retry to connector_service.
  const { hasPermission } = usePermissions();
  const canRetryStep = hasPermission(StandardPermission.Analytics.REPORTS);
  const canRetryProcessing = hasPermission(StandardPermission.Analytics.CONNECTORS);

  // Reset to page 1 when date range changes
  useEffect(() => { setPage(1); }, [from, to, setPage]);

  const [connector, setConnector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('interactions');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [pipelineData, setPipelineData] = useState({});
  const [retryingStep, setRetryingStep] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  // Fetch connector info
  useEffect(() => {
    const load = async () => {
      const res = await getConnector(connectorId);
      if (res.success) setConnector(res.data);
      else toast.error(res.error);
    };
    load();
  }, [connectorId]);

  // Fetch list data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'interactions') {
        const params = { page, page_size: pageSize, from_date: from, to_date: to };
        if (typeFilter) params.interaction_type = typeFilter;
        const res = await getInteractions(connectorId, params);
        if (res.success) {
          setData(res.data.items || []);
          setTotal(res.data.total || 0);
        } else {
          toast.error(res.error);
        }
      } else {
        const res = await getSyncHistory(connectorId, { page, page_size: pageSize });
        if (res.success) {
          setData(res.data.items || []);
          setTotal(res.data.total || 0);
        } else {
          toast.error(res.error);
        }
      }
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [connectorId, activeTab, page, pageSize, typeFilter, from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch pipeline status for completed interactions
  useEffect(() => {
    if (activeTab !== 'interactions') return;
    const completedItems = data.filter(i => i.processing_status === 'completed');
    if (completedItems.length === 0) return;
    let cancelled = false;

    const fetchStatuses = async () => {
      const results = {};
      await Promise.all(completedItems.map(async (item) => {
        try {
          const res = await getPipelineStatus(item.id);
          if (res.success && res.data?.steps) results[item.id] = res.data.steps;
        } catch (err) { console.warn('Pipeline status fetch failed for item', item.id, err); }
      }));
      if (!cancelled) setPipelineData(prev => ({ ...prev, ...results }));
    };
    fetchStatuses();
    return () => { cancelled = true; };
  }, [data, activeTab]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    reset();
    setTypeFilter('');
  };

  const handleRetryStep = async (item, stepType) => {
    setRetryingStep(stepType);
    const res = await retryPipelineStep(item.id, stepType);
    if (res.success) {
      toast.success(`${STEP_TOOLTIPS[stepType]} retry queued`);
      const statusRes = await getPipelineStatus(item.id);
      if (statusRes.success && statusRes.data?.steps) {
        setPipelineData(prev => ({ ...prev, [item.id]: statusRes.data.steps }));
      }
    } else {
      toast.error(res.error);
    }
    setRetryingStep(null);
  };

  const handleProcess = async (interactionId) => {
    setProcessingId(interactionId);
    const res = await retryInteractionProcessing(connectorId, interactionId);
    if (res.success) {
      toast.success(res.data.message);
      fetchData();
    } else {
      toast.error(res.error);
    }
    setProcessingId(null);
  };

  const filteredData = searchText && activeTab === 'interactions'
    ? data.filter((item) => {
        const q = searchText.toLowerCase();
        return [
          item.customer_identifier, item.agent_identifier, item.assigned_to, item.interaction_type,
          item.status, item.processing_status,
        ].some((v) => v && String(v).toLowerCase().includes(q));
      })
    : data;

  if (!connector && loading) return <Spinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/connectors')}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Back to connectors"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {connector?.display_name || 'Connector'} — Interactions
            </h2>
            <p className="text-xs text-gray-500">{total} records</p>
          </div>
        </div>
        <RefreshButton onClick={fetchData} loading={loading} />
      </div>

      {/* Tabs + Filter */}
      <div className="flex items-center justify-between mb-4 border-b border-gray-200">
        <div className="flex items-center space-x-1">
          <button
            onClick={() => switchTab('interactions')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'interactions'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Database size={14} className="inline mr-1" />
            Interactions
          </button>
          <button
            onClick={() => switchTab('history')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Clock size={14} className="inline mr-1" />
            Sync History
          </button>
        </div>
        {activeTab === 'interactions' && (
          <div className="flex items-center space-x-3 mb-1">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search interactions..."
                aria-label="Search interactions"
                className="pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 w-48"
              />
            </div>
            <DateRangeFilter from={from} to={to} onChange={onDateChange} />
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              aria-label="Filter by type"
              className="text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">All Types</option>
              <option value="call">Call</option>
              <option value="chat">Chat</option>
              <option value="email">Email</option>
            </select>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="card">
        {loading ? (
          <div className="p-8"><Spinner size="sm" /></div>
        ) : data.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={activeTab === 'interactions' ? Database : Clock}
              title={activeTab === 'interactions' ? 'No interactions found' : 'No sync history'}
              description={activeTab === 'interactions'
                ? 'Interactions will appear after a successful sync.'
                : 'Sync history will appear after the first sync run.'}
            />
          </div>
        ) : activeTab === 'interactions' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Type</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Customer</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Agent</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Duration</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Pipeline</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Analytics</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Date</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredData.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => navigate(`/connectors/${connectorId}/interactions/${item.id}`)}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium capitalize ${TYPE_COLORS[item.interaction_type] || 'bg-gray-100 text-gray-600'}`}>
                        {item.interaction_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {item.customer_identifier || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600" title={item.agent_identifier || ''}>
                      {agentLabel(item)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {formatDuration(item.duration_seconds)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs capitalize ${INTERACTION_STATUS_COLORS[item.status] || 'text-gray-600'}`}>
                        {item.status || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PROCESSING_STATUS_COLORS[item.processing_status] || 'bg-gray-100 text-gray-500'}`}>
                        {PROCESSING_STATUS_LABELS[item.processing_status] || item.processing_status || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {/* Step retry is an analytics_service POST
                          (analytics:reports). The trigger is a <span onClick>,
                          not a button, so the callback is withheld rather than
                          the element wrapped — the badge stays readable. */}
                      <AnalyticsBadges
                        pipelineSteps={pipelineData[item.id]}
                        retryingStep={retryingStep}
                        onRetry={canRetryStep ? (stepType) => handleRetryStep(item, stepType) : undefined}
                      />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {formatDate(item.occurred_at)}
                    </td>
                    <td className="px-4 py-3">
                      {/* This retry hits connector_service, not analytics_service
                          — a different permission from the badge retry above, on
                          the same row. */}
                      {canRetryProcessing
                        && (item.processing_status === 'audio_failed' || item.processing_status === 'transcription_failed') ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleProcess(item.id); }}
                          disabled={processingId === item.id}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                          title="Retry"
                        >
                          {processingId === item.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
                        </button>
                      ) : (
                        <ChevronRight size={14} className="text-gray-400 group-hover:text-primary-600" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Sync History Tab */
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Started</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Duration</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Fetched</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Stored</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Failed</th>
                  <th scope="col" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-500'}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{formatDate(item.started_at)}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{formatSyncDuration(item.started_at, item.completed_at)}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 font-medium">{item.records_fetched}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 font-medium">{item.records_stored}</td>
                    <td className={`px-4 py-3 text-xs font-medium ${item.records_failed > 0 ? 'text-red-600' : 'text-gray-700'}`}>{item.records_failed}</td>
                    <td className="px-4 py-3 text-xs text-gray-500" title={item.error_message || ''}>
                      {item.error_message
                        ? (item.error_message.length > 40 ? `${item.error_message.slice(0, 40)}...` : item.error_message)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4">
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default ConnectorInteractionsPage;
